'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    id: 'quote-time',
    question: 'How long does it take to receive a quote?',
    answer: 'We guarantee a response within 24 business hours for all quote requests. For urgent inquiries, please call our direct line at +1 (800) 248-2633 for immediate assistance from our sales team.'
  },
  {
    id: 'consultation',
    question: 'Is the initial consultation free?',
    answer: 'Yes, all initial consultations are complimentary. Our equipment specialists will assess your facility needs, discuss technical requirements, and provide expert recommendations at no cost or obligation.'
  },
  {
    id: 'installation',
    question: 'Do you provide installation services?',
    answer: 'Absolutely. We offer comprehensive installation services including equipment setup, calibration, staff training, and compliance documentation. Installation can be included in your quote or arranged separately.'
  },
  {
    id: 'warranty',
    question: 'What warranty coverage is included?',
    answer: 'All equipment comes with manufacturer warranties, and we offer extended warranty options and service contracts. Specific coverage details vary by equipment type and will be outlined in your quote.'
  },
  {
    id: 'financing',
    question: 'Do you offer financing options?',
    answer: 'Yes, we partner with leading medical equipment financing companies to offer flexible payment plans, leasing options, and equipment financing tailored to healthcare facilities of all sizes.'
  },
  {
    id: 'international',
    question: 'Can you ship internationally?',
    answer: 'Yes, we serve international customers and have established distribution networks worldwide. Contact our international division at international@aivamedical.com for shipping options and regulatory compliance assistance.'
  }
];

const FAQSection: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-surface">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/2 mx-auto mb-12"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-text-secondary">
            Quick answers to common questions about our services and processes
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-card border border-border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-brand"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-250"
              >
                <span className="text-lg font-semibold text-text-primary pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={20}
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openFAQ === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openFAQ === faq.id ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-4 pt-2 text-text-secondary leading-relaxed border-t border-border">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">
            Still have questions? We're here to help!
          </p>
          <a
            href="mailto:support@aivamedical.com"
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-primary hover:bg-secondary rounded-lg transition-all duration-250"
          >
            <Icon name="EnvelopeIcon" size={18} className="mr-2" />
            Contact Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;